import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ClipboardList, Plus, CheckCircle, X, User, AlertCircle, Trash2, Tag, ChevronDown, ChevronUp, Pencil, Save } from 'lucide-react';
import { loadAccountsFromServer } from './Login';
import { CustomerSearchSelect, ItemSearchSelect } from './CommonComponents';

const STATUS_LABEL = { pending: '대기중', assigned: '배정됨', delivered: '배달완료' };
const STATUS_COLOR = { pending: '#D97706', assigned: '#7C3AED', delivered: '#059669' };
const STATUS_BG    = { pending: '#FEF3C7', assigned: '#F3E8FF', delivered: '#D1FAE5' };

const EMPTY_FORM = {
  customer: '',
  note: '',
  deliveryDate: new Date().toISOString().split('T')[0],
};

const OrderManagement = ({ customers = [], items = [], orders = [], setOrders, cylinders = [], setCylinders, deliveries = [], setDeliveries, specialPrices = [] }) => {
  const itemNames = useMemo(() => items.map(i => i.name), [items]);

  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [itemRows, setItemRows] = useState(() => [{ item: itemNames[0] ?? '', quantity: 1 }]);
  const [formError, setFormError] = useState('');
  const [saved, setSaved] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDriver, setFilterDriver] = useState('');
  const [filterDate,   setFilterDate]   = useState('');

  // 수정 상태
  const [editingId,   setEditingId]   = useState(null);
  const [editForm,    setEditForm]    = useState({});
  const [editItemRows, setEditItemRows] = useState([]);

  // 용기번호 수기 입력
  const [cylinderMap,  setCylinderMap]  = useState({});
  const [openCylPanel, setOpenCylPanel] = useState(new Set());
  const [cylInputMap,  setCylInputMap]  = useState({});
  const cylInputRefs = useRef({});

  useEffect(() => {
    loadAccountsFromServer()
      .then(accs => setDrivers(accs.filter(a => a.role === 'driver')));
  }, []);

  useEffect(() => {
    setItemRows(prev => {
      if (prev.length === 0 || (prev.length === 1 && !prev[0].item && itemNames.length > 0)) {
        return [{ item: itemNames[0] ?? '', quantity: 1 }];
      }
      return prev;
    });
  }, [itemNames]);

  const filteredOrders = useMemo(() => {
    return [...orders]
      .reverse()
      .filter(o => {
        if (filterStatus && o.status !== filterStatus) return false;
        if (filterDriver && o.assignedDriverId !== filterDriver) return false;
        if (filterDate && o.deliveryDate !== filterDate) return false;
        return true;
      });
  }, [orders, filterStatus, filterDriver, filterDate]);

  const pendingCount   = orders.filter(o => o.status === 'pending').length;
  const assignedCount  = orders.filter(o => o.status === 'assigned').length;
  const deliveredCount = orders.filter(o => o.status === 'delivered').length;

  // ── 주문 접수 폼 헬퍼 ────────────────────────────────────────
  const updateRow = (idx, field, value) =>
    setItemRows(prev => prev.map((r, i) => i !== idx ? r : { ...r, [field]: value }));
  const addRow    = () => setItemRows(prev => [...prev, { item: itemNames[0] ?? '', quantity: 1 }]);
  const removeRow = (idx) => setItemRows(prev => prev.filter((_, i) => i !== idx));
  const totalQuantity = itemRows.reduce((s, r) => s + (r.quantity || 0), 0);

  // ── 수정 폼 헬퍼 ──────────────────────────────────────────────
  const updateEditRow = (idx, field, value) =>
    setEditItemRows(prev => prev.map((r, i) => i !== idx ? r : { ...r, [field]: value }));
  const addEditRow    = () => setEditItemRows(prev => [...prev, { item: itemNames[0] ?? '', quantity: 1 }]);
  const removeEditRow = (idx) => setEditItemRows(prev => prev.filter((_, i) => i !== idx));

  const startEdit = (o) => {
    setEditingId(o.id);
    setEditForm({ customer: o.customer, deliveryDate: o.deliveryDate, note: o.note || '' });
    const existingItems = o.items
      ? Object.entries(o.items).map(([item, quantity]) => ({ item, quantity }))
      : [{ item: o.item || '', quantity: o.quantity || 1 }];
    setEditItemRows(existingItems);
    // 수정 열리면 용기 패널 닫기
    setOpenCylPanel(prev => { const n = new Set(prev); n.delete(o.id); return n; });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); setEditItemRows([]); };

  const saveEdit = (o) => {
    const isDelivered = o.status === 'delivered';

    if (isDelivered) {
      // 완료 주문: 특이사항·날짜·거래처만 수정
      setOrders(prev => prev.map(ord =>
        ord.id === o.id
          ? { ...ord, note: editForm.note, deliveryDate: editForm.deliveryDate, customer: editForm.customer }
          : ord
      ));
    } else {
      // 미완료 주문: 전체 수정
      const validRows = editItemRows.filter(r => r.item && (r.quantity || 0) >= 1);
      if (validRows.length === 0) return;
      const newItems = {};
      let newQty = 0;
      for (const row of validRows) {
        newItems[row.item] = (newItems[row.item] || 0) + row.quantity;
        newQty += row.quantity;
      }
      setOrders(prev => prev.map(ord =>
        ord.id === o.id
          ? {
              ...ord,
              customer:     editForm.customer,
              deliveryDate: editForm.deliveryDate,
              note:         editForm.note,
              items:        newItems,
              item:         Object.keys(newItems).length === 1 ? Object.keys(newItems)[0] : '혼합',
              quantity:     newQty,
            }
          : ord
      ));
    }
    cancelEdit();
  };

  // ── 주문 등록 ──────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.customer) { setFormError('거래처를 선택하세요.'); return; }
    const validRows = itemRows.filter(r => r.item && (r.quantity || 0) >= 1);
    if (validRows.length === 0) { setFormError('품목과 수량을 입력하세요.'); return; }

    const itemsMap = {};
    let qty = 0;
    for (const row of validRows) {
      itemsMap[row.item] = (itemsMap[row.item] || 0) + row.quantity;
      qty += row.quantity;
    }

    const order = {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      deliveryDate: form.deliveryDate,
      customer: form.customer,
      items: itemsMap,
      item: Object.keys(itemsMap).length === 1 ? Object.keys(itemsMap)[0] : '혼합',
      quantity: qty,
      note: form.note,
      status: 'pending',
      assignedDriver: '',
      assignedDriverId: '',
      createdAt: Date.now(),
    };

    setOrders(prev => [...prev, order]);
    setForm({ ...EMPTY_FORM });
    setItemRows([{ item: itemNames[0] ?? '', quantity: 1 }]);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  // ── 기사 배정 ──────────────────────────────────────────────────
  const assignDriver = (orderId, driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    setOrders(prev => prev.map(o =>
      o.id === orderId
        ? { ...o, assignedDriverId: driverId, assignedDriver: driver?.name ?? '', status: driverId ? 'assigned' : 'pending' }
        : o
    ));
  };

  // ── 용기번호 패널 ──────────────────────────────────────────────
  const toggleCylPanel = (orderId) => {
    setOpenCylPanel(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) { next.delete(orderId); }
      else {
        next.add(orderId);
        setTimeout(() => cylInputRefs.current[orderId]?.focus(), 80);
      }
      return next;
    });
  };

  const addCylinder = (orderId) => {
    const val = (cylInputMap[orderId] || '').trim();
    if (!val) return;
    setCylinderMap(prev => {
      const existing = prev[orderId] || [];
      if (existing.includes(val)) return prev;
      return { ...prev, [orderId]: [...existing, val] };
    });
    setCylInputMap(prev => ({ ...prev, [orderId]: '' }));
    setTimeout(() => cylInputRefs.current[orderId]?.focus(), 30);
  };

  const removeCylinder = (orderId, idx) =>
    setCylinderMap(prev => ({
      ...prev,
      [orderId]: (prev[orderId] || []).filter((_, i) => i !== idx),
    }));

  // ── 배달완료 처리 ──────────────────────────────────────────────
  const markDelivered = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    const manualIds = cylinderMap[orderId] || [];
    let deliveredIds = [];
    const itemsMap = order.items || { [order.item]: order.quantity };

    if (manualIds.length > 0) {
      const known = manualIds.filter(id => cylinders.find(c => c.id === id));
      if (known.length > 0) {
        setCylinders(prev => prev.map(c =>
          known.includes(c.id) ? { ...c, locType: 'customer', location: order.customer } : c
        ));
      }
      deliveredIds = manualIds;
    } else {
      for (const [itemName, qty] of Object.entries(itemsMap)) {
        const available = cylinders.filter(
          c => c.locType === 'warehouse' && c.status === '실병' && c.item === itemName
        );
        const ids = available.slice(0, qty).map(c => c.id);
        deliveredIds = [...deliveredIds, ...ids];
      }
      if (deliveredIds.length > 0) {
        setCylinders(prev => prev.map(c =>
          deliveredIds.includes(c.id) ? { ...c, locType: 'customer', location: order.customer } : c
        ));
      }
    }

    const customerObj = customers.find(c => c.name === order.customer);
    let totalAmt = 0;
    for (const [itemName, qty] of Object.entries(itemsMap)) {
      let price = 0;
      if (customerObj) {
        const sp = specialPrices.find(s => s.customerId === customerObj.id && s.item === itemName);
        if (sp) price = sp.price;
      }
      if (!price) {
        const itemObj = items.find(i => i.name === itemName);
        price = itemObj?.standardPrice ?? 0;
      }
      totalAmt += qty * price;
    }

    setDeliveries(prev => [...prev, {
      id: Date.now(),
      customer: order.customer,
      warehouse: '',
      item: order.item,
      items: { ...itemsMap },
      quantity: order.quantity,
      price: 0,
      amount: totalAmt,
      date: order.deliveryDate || new Date().toISOString().split('T')[0],
      cylinders: deliveredIds,
      fromOrderId: orderId,
    }]);

    setOrders(prev => prev.map(o =>
      o.id === orderId
        ? { ...o, status: 'delivered', deliveredBarcodes: deliveredIds, deliveredAt: new Date().toISOString() }
        : o
    ));
    setCylinderMap(prev => { const n = { ...prev }; delete n[orderId]; return n; });
    setOpenCylPanel(prev => { const n = new Set(prev); n.delete(orderId); return n; });
  };

  const deleteOrder = (orderId) => {
    if (!window.confirm('이 주문을 삭제하시겠습니까?')) return;
    setOrders(prev => prev.filter(o => o.id !== orderId));
    setCylinderMap(prev => { const n = { ...prev }; delete n[orderId]; return n; });
  };

  const getItemsMap = (o) => o.items || (o.item ? { [o.item]: o.quantity } : {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* 요약 카드 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        {[
          { label: '대기중',   count: pendingCount,   color: '#D97706', bg: '#FEF3C7' },
          { label: '배정됨',   count: assignedCount,  color: '#7C3AED', bg: '#F3E8FF' },
          { label: '배달완료', count: deliveredCount,  color: '#059669', bg: '#D1FAE5' },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', borderRadius: 14, padding: '18px 20px', border: `1px solid ${s.color}33`, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: s.color }}>{s.count}</div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* 주문 접수 폼 */}
      <div style={sectionBox}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#185FA5', marginBottom: 22 }}>
          <Plus size={22} />
          <h2 style={{ margin: 0, fontSize: 19 }}>주문 접수</h2>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>거래처명</label>
              <CustomerSearchSelect
                customers={customers}
                value={form.customer}
                onChange={name => setForm(p => ({ ...p, customer: name }))}
              />
            </div>
            <div>
              <label style={labelStyle}>배달 예정일</label>
              <input type="date" style={inputStyle} value={form.deliveryDate}
                onChange={e => setForm(p => ({ ...p, deliveryDate: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>특이사항</label>
              <input type="text" style={inputStyle} placeholder="특이사항 입력 (선택)" value={form.note}
                onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={labelStyle}>품목 / 수량</label>
              <button type="button" onClick={addRow} style={addRowBtn}>
                <Plus size={13} /> 품목 추가
              </button>
            </div>
            <div style={rowHeaderStyle}>
              <span>품목</span>
              <span style={{ textAlign: 'center' }}>수량(본)</span>
              <span></span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {itemRows.map((row, idx) => (
                <div key={idx} style={rowGridStyle}>
                  <ItemSearchSelect itemNames={itemNames} value={row.item}
                    onChange={name => updateRow(idx, 'item', name)} placeholder="품목 검색 또는 선택" />
                  <input type="number" min={1}
                    style={{ ...inputStyle, padding: '9px 10px', fontSize: 13, textAlign: 'center' }}
                    value={row.quantity || ''}
                    onChange={e => updateRow(idx, 'quantity', parseInt(e.target.value) || 0)} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {itemRows.length > 1 && (
                      <button type="button" onClick={() => removeRow(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 4, display: 'flex', alignItems: 'center' }}>
                        <X size={15} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalQuantity > 0 && (
              <div style={{ textAlign: 'right', fontSize: 13, color: '#185FA5', fontWeight: 700, marginTop: 8 }}>
                총 {totalQuantity}본
              </div>
            )}
          </div>

          {formError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
              <AlertCircle size={15} color="#DC2626" />
              <span style={{ fontSize: 13, color: '#DC2626', fontWeight: 600 }}>{formError}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button type="submit" style={{ ...submitBtn, flex: 1 }}>
              <ClipboardList size={18} /> 주문 등록
            </button>
            {saved && <span style={{ fontSize: 13, color: '#059669', fontWeight: 700, whiteSpace: 'nowrap' }}>✓ 주문 등록 완료!</span>}
          </div>
        </form>
      </div>

      {/* 주문 목록 */}
      <div style={sectionBox}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <ClipboardList size={18} color="#185FA5" />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>주문 목록</h3>
          <span style={{ fontSize: 13, color: '#888' }}>총 {orders.length}건</span>
        </div>

        {/* 필터 */}
        {orders.length > 0 && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center', padding: '12px 14px', background: '#f8f7f2', borderRadius: 10, border: '1px solid #e5e3db' }}>
            <div>
              <label style={filterLabelStyle}>상태</label>
              <select style={filterInputStyle} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">전체</option>
                <option value="pending">대기중</option>
                <option value="assigned">배정됨</option>
                <option value="delivered">배달완료</option>
              </select>
            </div>
            {drivers.length > 0 && (
              <div>
                <label style={filterLabelStyle}>배달기사</label>
                <select style={filterInputStyle} value={filterDriver} onChange={e => setFilterDriver(e.target.value)}>
                  <option value="">전체</option>
                  {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={filterLabelStyle}>배달 예정일</label>
              <input type="date" style={filterInputStyle} value={filterDate}
                onChange={e => setFilterDate(e.target.value)} />
            </div>
            {(filterStatus || filterDriver || filterDate) && (
              <button onClick={() => { setFilterStatus(''); setFilterDriver(''); setFilterDate(''); }}
                style={{ alignSelf: 'flex-end', padding: '7px 12px', background: 'none', border: '1px solid #d3d1c7', borderRadius: 7, fontSize: 12, color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <X size={12} /> 초기화
              </button>
            )}
          </div>
        )}

        {orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13, border: '1px dashed #e5e3db', borderRadius: 10 }}>
            등록된 주문이 없습니다
          </div>
        ) : filteredOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13, border: '1px dashed #e5e3db', borderRadius: 10 }}>
            검색 결과가 없습니다
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredOrders.map(o => {
              const enteredCyls = cylinderMap[o.id] || [];
              const isPanelOpen = openCylPanel.has(o.id);
              const orderItems  = getItemsMap(o);
              const isEditing   = editingId === o.id;
              const isDelivered = o.status === 'delivered';

              return (
                <div key={o.id} style={{ border: `1.5px solid ${isEditing ? '#185FA5' : '#e5e3db'}`, borderRadius: 12, overflow: 'hidden' }}>

                  {/* ── 수정 폼 ── */}
                  {isEditing ? (
                    <div style={{ padding: '18px 18px 16px', background: '#f0f5ff' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#185FA5', marginBottom: 14 }}>
                        주문 수정
                        {isDelivered && <span style={{ fontWeight: 400, color: '#888', marginLeft: 8, fontSize: 12 }}>· 완료된 주문은 거래처·날짜·특이사항만 수정 가능</span>}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={editLabelStyle}>거래처</label>
                          <CustomerSearchSelect
                            customers={customers}
                            value={editForm.customer}
                            onChange={name => setEditForm(p => ({ ...p, customer: name }))}
                            inputStyle={{ padding: '8px 10px', border: '1px solid #93c5fd', fontSize: 13, background: '#fff' }}
                          />
                        </div>
                        <div>
                          <label style={editLabelStyle}>배달 예정일</label>
                          <input type="date" style={editInputStyle}
                            value={editForm.deliveryDate}
                            onChange={e => setEditForm(p => ({ ...p, deliveryDate: e.target.value }))} />
                        </div>
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <label style={editLabelStyle}>특이사항</label>
                        <input type="text" style={{ ...editInputStyle, width: '100%', boxSizing: 'border-box' }}
                          placeholder="특이사항 (선택)"
                          value={editForm.note}
                          onChange={e => setEditForm(p => ({ ...p, note: e.target.value }))} />
                      </div>

                      {/* 품목 수정 (미완료 주문만) */}
                      {!isDelivered && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <label style={editLabelStyle}>품목 / 수량</label>
                            <button type="button" onClick={addEditRow}
                              style={{ ...addRowBtn, padding: '4px 10px', fontSize: 11 }}>
                              <Plus size={11} /> 품목 추가
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {editItemRows.map((row, idx) => (
                              <div key={idx} style={rowGridStyle}>
                                <ItemSearchSelect itemNames={itemNames} value={row.item}
                                  onChange={name => updateEditRow(idx, 'item', name)}
                                  placeholder="품목 검색"
                                  inputStyle={{ padding: '8px 10px', border: '1px solid #93c5fd', fontSize: 13, background: '#fff' }}
                                />
                                <input type="number" min={1}
                                  style={{ ...editInputStyle, textAlign: 'center', width: '100%', boxSizing: 'border-box' }}
                                  value={row.quantity || ''}
                                  onChange={e => updateEditRow(idx, 'quantity', parseInt(e.target.value) || 0)} />
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {editItemRows.length > 1 && (
                                    <button type="button" onClick={() => removeEditRow(idx)}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 4, display: 'flex' }}>
                                      <X size={15} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => saveEdit(o)}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 18px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                          <Save size={14} /> 저장
                        </button>
                        <button onClick={cancelEdit}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#fff', color: '#666', border: '1px solid #d3d1c7', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                          <X size={14} /> 취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* ── 주문 요약 행 ── */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: '#f8f7f2', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLOR[o.status], background: STATUS_BG[o.status], padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                          {STATUS_LABEL[o.status]}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#333', whiteSpace: 'nowrap' }}>{o.deliveryDate} 배달</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#185FA5', whiteSpace: 'nowrap' }}>{o.customer}</span>

                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', flex: 1 }}>
                          {Object.entries(orderItems).map(([itemName, qty]) => (
                            <span key={itemName} style={{ fontSize: 12, fontWeight: 700, background: '#e8f0fb', color: '#185FA5', padding: '3px 9px', borderRadius: 10 }}>
                              {itemName} {qty}본
                            </span>
                          ))}
                          {o.quantity > 0 && Object.keys(orderItems).length > 1 && (
                            <span style={{ fontSize: 12, color: '#888' }}>총 {o.quantity}본</span>
                          )}
                        </div>

                        {o.note && (
                          <span style={{ fontSize: 12, color: '#888', fontStyle: 'italic', whiteSpace: 'nowrap' }}>"{o.note}"</span>
                        )}
                        {o.assignedDriver && (
                          <span style={{ fontSize: 12, color: '#7C3AED', background: '#F3E8FF', padding: '2px 8px', borderRadius: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                            <User size={10} />{o.assignedDriver}
                          </span>
                        )}

                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          {!isDelivered && drivers.length > 0 && (
                            <select value={o.assignedDriverId || ''}
                              onChange={e => assignDriver(o.id, e.target.value)}
                              style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid #d3d1c7', fontSize: 12, background: '#fff', cursor: 'pointer' }}>
                              <option value="">기사 배정</option>
                              {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                          )}
                          {!isDelivered && (
                            <>
                              <button onClick={() => toggleCylPanel(o.id)} title="용기번호 수기 입력"
                                style={{ padding: '5px 10px', border: `1px solid ${isPanelOpen ? '#185FA5' : '#d3d1c7'}`, borderRadius: 7, fontSize: 12, cursor: 'pointer', background: isPanelOpen ? '#EEF5FF' : '#fff', color: isPanelOpen ? '#185FA5' : '#555', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                                <Tag size={13} />
                                용기번호
                                {enteredCyls.length > 0 && (
                                  <span style={{ background: '#185FA5', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>{enteredCyls.length}</span>
                                )}
                                {isPanelOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              </button>
                              <button onClick={() => markDelivered(o.id)}
                                style={{ padding: '5px 12px', background: '#059669', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <CheckCircle size={13} /> 배달완료
                              </button>
                            </>
                          )}
                          {/* 수정 버튼 */}
                          <button onClick={() => startEdit(o)} title="수정"
                            style={{ padding: '5px 8px', background: '#fff', border: '1px solid #d3d1c7', borderRadius: 7, fontSize: 12, color: '#185FA5', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => deleteOrder(o.id)}
                            style={{ padding: '5px 8px', background: 'none', border: '1px solid #fecaca', borderRadius: 7, fontSize: 12, color: '#DC2626', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>

                      {/* 용기번호 패널 */}
                      {isPanelOpen && !isDelivered && (
                        <div style={{ padding: '14px 16px', background: '#f0f5ff', borderTop: '1px solid #c7d9f5' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#185FA5', marginBottom: 10 }}>
                            용기번호 수기 입력
                            <span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>· 입력 없이 배달완료 클릭 시 창고 실병 자동 배정</span>
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                            <input ref={el => cylInputRefs.current[o.id] = el} type="text"
                              value={cylInputMap[o.id] || ''}
                              onChange={e => setCylInputMap(prev => ({ ...prev, [o.id]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCylinder(o.id); } }}
                              placeholder="용기번호 입력 후 Enter 또는 추가"
                              style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #185FA5', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'monospace' }} />
                            <button type="button" onClick={() => addCylinder(o.id)}
                              style={{ padding: '9px 16px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                              추가
                            </button>
                          </div>
                          {enteredCyls.length > 0 ? (
                            <>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 150, overflowY: 'auto' }}>
                                {enteredCyls.map((cyl, idx) => {
                                  const isRegistered = cylinders.find(c => c.id === cyl);
                                  return (
                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'white', padding: '6px 10px', borderRadius: 7, border: `1px solid ${isRegistered ? '#c7d9f5' : '#fecaca'}` }}>
                                      <CheckCircle size={13} color={isRegistered ? '#185FA5' : '#DC2626'} />
                                      <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, flex: 1, color: isRegistered ? '#1e293b' : '#DC2626' }}>{cyl}</span>
                                      {!isRegistered && <span style={{ fontSize: 11, color: '#DC2626', background: '#fef2f2', padding: '1px 6px', borderRadius: 6 }}>미등록</span>}
                                      <button type="button" onClick={() => removeCylinder(o.id, idx)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 2, display: 'flex' }}>
                                        <X size={13} />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 12, color: '#555' }}>
                                  총 <b>{enteredCyls.length}</b>개 입력됨
                                  {enteredCyls.length !== o.quantity && (
                                    <span style={{ color: '#D97706', marginLeft: 6 }}>(주문수량 {o.quantity}본과 다름)</span>
                                  )}
                                </span>
                                <button type="button" onClick={() => setCylinderMap(prev => ({ ...prev, [o.id]: [] }))}
                                  style={{ fontSize: 12, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                                  전체 삭제
                                </button>
                              </div>
                            </>
                          ) : (
                            <div style={{ fontSize: 12, color: '#aaa', textAlign: 'center', padding: '8px 0' }}>입력된 용기번호가 없습니다</div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* 접수일 푸터 */}
                  <div style={{ padding: '8px 16px', background: 'white', fontSize: 12, color: '#aaa' }}>
                    접수일: {o.date} · {new Date(o.createdAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                    {o.deliveredBarcodes?.length > 0 && (
                      <span style={{ marginLeft: 12, color: '#185FA5' }}>용기 {o.deliveredBarcodes.length}개 기록됨</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const labelStyle       = { display: 'block', fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 8 };
const inputStyle       = { width: '100%', padding: '12px', borderRadius: 8, border: '1px solid #d3d1c7', fontSize: 14, boxSizing: 'border-box', background: '#fcfbf7' };
const submitBtn        = { padding: '14px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 };
const sectionBox       = { background: 'white', padding: 28, borderRadius: 16, border: '1px solid #d3d1c7' };
const filterLabelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 4 };
const filterInputStyle = { padding: '7px 10px', borderRadius: 7, border: '1px solid #d3d1c7', fontSize: 13, background: '#fff', boxSizing: 'border-box' };
const rowGridStyle     = { display: 'grid', gridTemplateColumns: '2fr 120px 36px', gap: 8, alignItems: 'center' };
const rowHeaderStyle   = { ...rowGridStyle, padding: '6px 10px', background: '#f0f4f8', borderRadius: 6, fontSize: 12, color: '#666', fontWeight: 600, marginBottom: 4 };
const addRowBtn        = { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#e8f0fb', color: '#185FA5', border: '1px solid #185FA533', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const editLabelStyle   = { display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 5 };
const editInputStyle   = { padding: '8px 10px', borderRadius: 7, border: '1px solid #93c5fd', fontSize: 13, background: '#fff', boxSizing: 'border-box' };

export default OrderManagement;
